import { useDispatch, useSelector } from 'react-redux';
import React, { useState, useEffect, useMemo, JSX } from 'react';
import { PATHS } from 'src/common/constants';
import Breadcrumb from '../../../components/Breadcrumb';
import Button from '../../../components/Button';
import Details from '../../../components/Details';
import FontAwesomeIcon from '../../../components/Icon';
import Overlay from '../../../components/Overlay';
import { deleteContactById } from 'src/store/actions/contacts';
import {
  onClearMoreDetails,
  onFetchContactMoreDetails,
} from 'src/store/actions/suites';
import Loader from '../../../components/Loader';
import ContactMoreDetails from '../ContactMoreDetails';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { useHistory } from 'react-router-dom';
import '../ContactDetails.scss';
import {
  ContactFormActionsContext,
  useContactFormActionsFromContext,
} from 'src/SharedCommonContext/ContactFormContext';
import ContactForm from 'src/components/ContactForm/ContactForm';
import { enrichContactDataForEdit } from 'src/utils/CommonHelpers/commonhelpers';
import {
  useContactFormSubmissionContacts
} from 'src/pages/SharedPageCustomHooks/UseContactSaveContactsPage/useContactFormSubmission';
import { buildContactDetailsItems } from 'src/pages/Contact/ContactDetails/helpers';

const ContactDetails = ({
  contactData,
  sendResetPasswordLinkToUser,
  sendActivationLinkToUser,
  navFrom,
  counts,
  isFromDashBoard,
  refetchContactFormData,
}: any) => {
  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const {
    clearContactFormData,
    initializeContactFormDataFromEdit,
  }: ContactFormActionsContext = useContactFormActionsFromContext();

  const dispatch = useDispatch();
  const history = useHistory();

  const moreDetails = useSelector((state: any) => state.moreDetails);
  const user = useSelector((state: any) => selectAuthenticatedUser(state));

  const onDeleteContact = (contact: any) => {
    dispatch(deleteContactById(contact.id, history));
  };

  useEffect(() => {
    dispatch(onClearMoreDetails());
  }, []);

  const onFetchMoreDetails = () => {
    dispatch(onFetchContactMoreDetails(contactData.id));
  };

  const handleEditClick = () => {
    setShowOverlay(true);
    const enrichedContactData: any = enrichContactDataForEdit(contactData);
    initializeContactFormDataFromEdit(enrichedContactData);
  };

  const handleCloseForm = () => {
    setShowOverlay(false);
    clearContactFormData();
  };
  
  const { handleSubmitContactForm } = useContactFormSubmissionContacts({
    onSuccess: () => refetchContactFormData(),
    onClose: handleCloseForm,
  });

  const detailsItems: JSX.Element[] = useMemo(
    () => buildContactDetailsItems({
      contactData,
      counts,
      user,
      isOpen,
      setIsOpen,
      onFetchMoreDetails,
    }),
    [contactData, counts, user, isOpen]
  );

  return (
    <div className="contact-details">
      <div className="contact-details__top">
        <div className="contact-details__breadcrumb">
          {isFromDashBoard === true ? (
            <Breadcrumb
              label="MY HOME"
              to={{
                pathname: PATHS.SUITES_VIEW.replace(
                  ':id',
                  localStorage.getItem('propertyId') +
                    '&' +
                    localStorage.getItem('suiteId')
                ),
                state: {
                  suite: { id: localStorage.getItem('suiteId') },
                  property: { id: localStorage.getItem('propertyId') },
                  activeTab: 0,
                  from: 'Dashboard',
                },
              }}
            />
          ) : navFrom !== '' ? (
            <Breadcrumb
              label="Service Request"
              to={PATHS.SERVICE_REQUESTS_VIEW.replace(':id', navFrom)}
            />
          ) : (
            <Breadcrumb label="Contacts" to={PATHS.CONTACTS_LIST} />
          )}
        </div>
      </div>
      <div className="contact-details__bottom">
        <div className="contact-details__actions">
          <Button
            color="semantic"
            onClick={handleEditClick}
            label={<FontAwesomeIcon glyph="pencil" weight="light" />}
          />
          <Button
            color="semantic"
            onClick={() =>
              window.confirm(
                `Are you sure you want to delete ${contactData.firstname} ${contactData.lastname}?`
              ) && onDeleteContact(contactData)
            }
            label={<FontAwesomeIcon glyph="trash" weight="light" />}
          />
        </div>
        <Details items={detailsItems} />
      </div>
      <Overlay
        showOverlay={showOverlay}
        content={
          <ContactForm
            onClose={handleCloseForm}
            onSubmit={handleSubmitContactForm}
            sendResetPasswordLinkToUser={sendResetPasswordLinkToUser}
            sendActivationLinkToUser={sendActivationLinkToUser}
            pageUsedFrom="Contacts"
          />
        }
      />
      {moreDetails && moreDetails.isEnableLoader ? (
        <Loader center={true} />
      ) : null}
      {moreDetails && moreDetails.isMoreDetails ? (
        <ContactMoreDetails
          closePopup={() => dispatch(onClearMoreDetails())}
          details={moreDetails.moreData}
        />
      ) : null}
    </div>
  );
};

export default ContactDetails;
